import { Instagram, Linkedin, Phone, Mail, MessageCircle, Facebook, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { PHONE, PHONE_LINK, WHATSAPP_LINK, INSTAGRAM_LINK, INSTAGRAM2_LINK, LINKEDIN_LINK, FACEBOOK_LINK, SISTER_BRANDS } from "../lib/data";
import { Logo } from "./Logo";
import { Reveal } from "./Reveal";

const Col = ({ title, children }) => (
  <div>
    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/80">{title}</p>
    <ul className="mt-5 space-y-3 text-sm text-neutral-400">{children}</ul>
  </div>
);

const linkCls = "inline-flex items-center gap-2.5 whitespace-nowrap hover:text-champagne transition-colors";

export const Footer = ({ scrollTo }) => {
  const year = new Date().getFullYear();
  const socials = [
    { name: "Instagram · Sanvalio", href: INSTAGRAM_LINK, icon: Instagram, testId: "footer-instagram-link" },
    { name: "Instagram · Design4You", href: INSTAGRAM2_LINK, icon: Instagram, testId: "footer-instagram2-link" },
    { name: "LinkedIn · Deepankshi", href: LINKEDIN_LINK, icon: Linkedin, testId: "footer-linkedin-link" },
    { name: "Facebook", href: FACEBOOK_LINK, icon: Facebook, testId: "footer-facebook-link" },
  ];
  return (
    <footer className="mt-24 border-t border-white/10 bg-surface/40" data-testid="footer">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 py-16 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <button data-testid="footer-wordmark" onClick={() => scrollTo("#top")} className="block text-left">
              <Logo size="lg" />
            </button>
            <p className="mt-6 max-w-sm text-sm font-light text-neutral-500 leading-relaxed">
              Live online courses in marketing, design, branding and freelancing — with certificates, diplomas and a mentor who has been teaching since 2015.
            </p>
          </div>

          <div className="lg:col-span-8 grid grid-cols-2 gap-10 sm:grid-cols-[0.9fr_0.9fr_1.5fr_1.2fr] sm:gap-8">
            <Col title="Learn">
              <li><button data-testid="footer-link-courses" onClick={() => scrollTo("#courses")} className={linkCls}>All Courses</button></li>
              <li><button data-testid="footer-link-outcomes" onClick={() => scrollTo("#outcomes")} className={linkCls}>Career Outcomes</button></li>
              <li><button data-testid="footer-link-founder" onClick={() => scrollTo("#founder")} className={linkCls}>About the Founder</button></li>
              <li><button data-testid="footer-link-brand-owners" onClick={() => scrollTo("#brand-owners")} className={linkCls}>For Brand Owners</button></li>
            </Col>
            <Col title="Sister brands">
              {SISTER_BRANDS.map((b) => (
                <li key={b.name}>
                  <a data-testid={`footer-brand-${b.name.toLowerCase().replace(/\s+/g, "-")}`} href={b.url} target="_blank" rel="noopener noreferrer" className={linkCls}>
                    {b.name} ↗
                  </a>
                </li>
              ))}
            </Col>
            <Col title="Contact">
              <li>
                <a data-testid="footer-phone-link" href={PHONE_LINK} className={linkCls}>
                  <Phone size={13} className="text-gold shrink-0" /> {PHONE}
                </a>
              </li>
              <li>
                <a data-testid="footer-email-link" href="mailto:support@sanvalioacademy.com" className={`${linkCls} text-[13px]`}>
                  <Mail size={13} className="text-gold shrink-0" /> support@sanvalioacademy.com
                </a>
              </li>
              <li>
                <a data-testid="footer-whatsapp-link" href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className={linkCls}>
                  <MessageCircle size={13} className="text-gold shrink-0" /> WhatsApp us
                </a>
              </li>
            </Col>
            <Col title="Follow">
              {socials.map((s) => (
                <li key={s.testId}>
                  <a data-testid={s.testId} href={s.href} target="_blank" rel="noopener noreferrer" className={linkCls}>
                    <s.icon size={13} className="text-gold shrink-0" /> {s.name}
                  </a>
                </li>
              ))}
            </Col>
          </div>
        </div>

        <Reveal className="mt-20">
          <div className="relative overflow-hidden rounded-3xl border border-gold/30 bg-gradient-to-br from-gold/[0.12] via-transparent to-gold/[0.06] px-6 py-12 sm:px-12 sm:py-16" data-testid="footer-manifesto">
            <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-gold/20 blur-3xl" />
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold/90">✦ A note from the founder</p>
            <h3 className="mt-6 max-w-4xl font-display text-2xl sm:text-4xl lg:text-5xl font-bold leading-[1.15] text-neutral-100">
              Success will not wait for you. If you don't run towards it,{" "}
              <em className="not-italic text-gold-gradient">someone else will.</em>
            </h3>
            <p className="mt-6 max-w-2xl font-display text-lg sm:text-2xl font-semibold leading-snug text-champagne/90">
              How much you earn — and whether you are financially free in the next 5 years — depends only on you.
            </p>
            <p className="mt-3 font-display text-xl sm:text-3xl font-bold tracking-tight text-neutral-100">
              So don't wait. <span className="text-gold-gradient">Join now.</span>
            </p>
            <motion.button
              data-testid="footer-join-now-button"
              onClick={() => scrollTo("#courses")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.96 }}
              className="btn-shine mt-9 inline-flex items-center gap-2 rounded-full bg-leaf px-8 py-4 text-sm font-bold text-void shadow-[0_10px_34px_-8px_rgba(232,193,90,0.7)] hover:bg-champagne transition-colors"
            >
              Join Now — Start Your Journey <ArrowRight size={16} />
            </motion.button>
          </div>
        </Reveal>

        <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-white/10 pt-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600">
            © {year} Sanvalio Academy · Registered 2024 · Teaching since 2015
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600">
            Made with passion by Sanvalio Studio
          </p>
        </div>
      </div>
    </footer>
  );
};
